import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  Renderer2,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SidebarService } from './sidebar.service';
import { SidebarOpenConfigInterface } from './sidebar-open-config.interface';
import { SidebarContainerInterface } from './sidebar-container.interface';

interface ContainerWithElementRef {
  _elementRef?: ElementRef;
}

@Component({
  standalone: true,
  selector: 'sandoz-sidebar',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class SidebarComponent<
  ContainerInterface extends SidebarContainerInterface,
> {
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);
  private readonly sidebarService = inject(SidebarService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private _cssClass?: string;
  private _container!: ContainerInterface;

  @Input({ required: true }) sidebarId!: string;
  @Input({ required: true })
  set container(value: ContainerInterface) {
    this._container = value;
    this._container.openedChange
      .asObservable()
      .pipe(
        filter(opened => !opened),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.sidebarService.close(this.sidebarId);
        this.clear();
      });
  }

  constructor() {
    this.sidebarService.openTriggered$.subscribe(config => {
      this.openIfApplicable(config).then();
    });
    this.sidebarService.closeTriggered$.subscribe(sidebarId => {
      this.closeIfApplicable(sidebarId).then();
    });
  }

  async closeIfApplicable(sidebarId: string): Promise<void> {
    if (sidebarId === this.sidebarId) {
      await this.close();
    }
  }

  async close(): Promise<void> {
    await this._container.close();
  }

  clear(): void {
    this.viewContainerRef?.clear();
    if (this._cssClass) {
      this.removeCssClass();
    }
  }

  async openIfApplicable(
    config: SidebarOpenConfigInterface<ContainerInterface, unknown>,
  ): Promise<void> {
    if (config.id === this.sidebarId) {
      this.clear();

      const componentRef = this.viewContainerRef.createComponent(
        config.componentClass,
      );
      if (config.cssClass) {
        this.addCssClass(config.cssClass);
      }

      componentRef.instance.setInputData(config.inputData);
      componentRef.instance.sidebarId = this.sidebarId;
      if (config.containerInit) {
        config.containerInit(this._container);
      }
      await this._container.open();
    }
  }

  private addCssClass(cssClass: string): void {
    this._cssClass = cssClass;
    this.renderer.addClass(
      (this._container as ContainerWithElementRef)._elementRef?.nativeElement,
      this._cssClass,
    );
  }

  private removeCssClass(): void {
    if (this._cssClass) {
      this.renderer.removeClass(
        (this._container as ContainerWithElementRef)._elementRef?.nativeElement,
        this._cssClass,
      );
      this._cssClass = undefined;
    }
  }
}
